import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  getTeacherCohortPresentation,
  getTeacherPresentation,
  type TeacherPresentationRecord,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { PresentationDeck, PresentationDeckNav } from "../teacher/PresentationDeck";
import { PresentationIcon } from "../teacher/PresentationIcon";
import { triggerPresentationPdfDownload } from "../teacher/triggerPresentationPdfDownload";
import {
  liveCodeForSlide,
  PresentationPresentView,
  type PresentationLiveCodeMap,
} from "../teacher/PresentationPresentView";

function exitFullscreen(): Promise<void> {
  const doc = document as Document & { webkitExitFullscreen?: () => Promise<void> | void };
  if (!document.fullscreenElement && !("webkitFullscreenElement" in document)) {
    return Promise.resolve();
  }
  const fn = document.exitFullscreen?.bind(document) ?? doc.webkitExitFullscreen?.bind(document);
  if (!fn) return Promise.resolve();
  return Promise.resolve(fn()).then(() => undefined);
}

export default function TeacherPresentationPreviewPage() {
  const { courseId, cohortId, presentationId } = useParams<{
    courseId?: string;
    cohortId?: string;
    presentationId: string;
  }>();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo")?.trim() || "";
  const startPresent = searchParams.get("present") === "1";
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const [presentation, setPresentation] = useState<TeacherPresentationRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [index, setIndex] = useState(0);
  const [presenting, setPresenting] = useState(false);
  const [chromeVisible, setChromeVisible] = useState(true);
  const [liveCodeBySlide, setLiveCodeBySlide] = useState<PresentationLiveCodeMap>({});
  const presentRootRef = useRef<HTMLDivElement>(null);
  const hideChromeTimer = useRef<number | null>(null);
  const wasFullscreen = useRef(false);
  const autoStartedPresent = useRef(false);

  const slideCount = presentation?.slides.length ?? 0;

  const load = useCallback(async () => {
    if (!bearer || !presentationId) return;
    setLoading(true);
    setError(null);
    try {
      if (cohortId) {
        const { presentation: p } = await getTeacherCohortPresentation(bearer, cohortId, presentationId);
        setPresentation(p);
      } else if (courseId) {
        const { presentation: p } = await getTeacherPresentation(bearer, courseId, presentationId);
        setPresentation(p);
      } else {
        throw new Error("Missing course or cohort context");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load presentation");
    } finally {
      setLoading(false);
    }
  }, [bearer, cohortId, courseId, presentationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const goTo = useCallback(
    (next: number) => {
      if (slideCount <= 0) return;
      setIndex(Math.max(0, Math.min(slideCount - 1, next)));
    },
    [slideCount]
  );

  const stopPresenting = useCallback(() => {
    setPresenting(false);
    setChromeVisible(true);
    setLiveCodeBySlide({});
    void exitFullscreen();
  }, []);

  const startPresenting = useCallback(() => {
    setPresenting(true);
    setChromeVisible(true);
  }, []);

  useEffect(() => {
    if (!startPresent || autoStartedPresent.current || loading || !presentation || slideCount === 0) return;
    autoStartedPresent.current = true;
    setPresenting(true);
  }, [startPresent, loading, presentation, slideCount]);

  useEffect(() => {
    function onFsChange() {
      const active = Boolean(
        document.fullscreenElement ??
          (document as Document & { webkitFullscreenElement?: Element }).webkitFullscreenElement
      );
      if (wasFullscreen.current && !active) {
        setPresenting(false);
        setChromeVisible(true);
        setLiveCodeBySlide({});
      }
      wasFullscreen.current = active;
    }
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
    };
  }, []);

  useEffect(() => {
    if (!presenting) return;
    function isTypingTarget(target: EventTarget | null): boolean {
      if (!(target instanceof HTMLElement)) return false;
      if (target.closest(".cm-editor") || target.closest("textarea") || target.closest("input")) {
        return true;
      }
      return target.tagName === "TEXTAREA" || target.tagName === "INPUT" || target.isContentEditable;
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        stopPresenting();
        return;
      }
      if (isTypingTarget(e.target)) return;
      if (e.key === "ArrowRight" || e.key === "PageDown" || e.key === " ") {
        e.preventDefault();
        goTo(index + 1);
        return;
      }
      if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        goTo(index - 1);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [presenting, index, goTo, stopPresenting]);

  const bumpChrome = useCallback(() => {
    setChromeVisible(true);
    if (hideChromeTimer.current) window.clearTimeout(hideChromeTimer.current);
    hideChromeTimer.current = window.setTimeout(() => setChromeVisible(false), 2400);
  }, []);

  useEffect(() => {
    if (!presenting) return;
    bumpChrome();
    return () => {
      if (hideChromeTimer.current) window.clearTimeout(hideChromeTimer.current);
    };
  }, [presenting, bumpChrome, index]);

  if (!presentationId || (!courseId && !cohortId)) return <p>Missing course or cohort context.</p>;
  if (loading) return <p className="teacher-pad">Loading preview…</p>;
  if (!presentation) return <p className="teacher-pad">{error ?? "Presentation not found"}</p>;

  const slides = presentation.slides;
  const slide = slides[index] ?? slides[0];
  const resolvedCourseId = courseId ?? presentation.courseId ?? "";
  const editUrl = cohortId
    ? undefined
    : resolvedCourseId
      ? `/teacher/courses/${encodeURIComponent(resolvedCourseId)}/presentations/${encodeURIComponent(presentationId)}`
      : undefined;
  const backUrl =
    returnTo && returnTo.startsWith("/")
      ? returnTo
      : cohortId
        ? `/teacher/cohorts/${encodeURIComponent(cohortId)}`
        : resolvedCourseId
          ? `/teacher/courses/${encodeURIComponent(resolvedCourseId)}/manage`
          : "/teacher";

  const presentOverlay =
    presenting && slide
      ? createPortal(
          <PresentationPresentView
            title={presentation.title}
            slide={slide}
            index={index}
            total={slides.length}
            liveCode={liveCodeForSlide(slide, liveCodeBySlide)}
            onLiveCodeChange={(code) =>
              setLiveCodeBySlide((prev) => ({ ...prev, [slide.id]: code }))
            }
            onResetCode={() =>
              setLiveCodeBySlide((prev) => {
                const next = { ...prev };
                delete next[slide.id];
                return next;
              })
            }
            chromeVisible={chromeVisible}
            onMouseMove={bumpChrome}
            onPrev={() => goTo(index - 1)}
            onNext={() => goTo(index + 1)}
            onExit={stopPresenting}
            onDownloadPdf={() =>
              triggerPresentationPdfDownload({ title: presentation.title, slides })
            }
            presentRootRef={presentRootRef}
          />,
          document.body
        )
      : null;

  return (
    <div className="teacher-quiz-preview teacher-pad presentation-preview">
      {presentOverlay}
      <header className="teacher-quiz-editor-head">
        <PresentationIcon
          title={presentation.title}
          iconName={presentation.iconName}
          bgColor={presentation.iconBgColor}
          fgColor={presentation.iconFgColor}
        />
        <div className="teacher-quiz-editor-head-copy">
          <h1 className="teacher-panel-title">{presentation.title}</h1>
          <p className="teacher-panel-lede">{presentation.description ?? "Presentation preview"}</p>
        </div>
      </header>
      <p className="muted small">
        Speaker notes stay on this preview for you. Use Present for a full-screen deck without notes,
        then share that screen. Slides with code get a live editor and console. Arrow keys move slides.
        Esc exits. Download PDF saves the slides only: no notes, console, or Run controls.
      </p>
      {slide ? (
        <PresentationDeck slide={slide} index={index} total={slides.length} showNotes />
      ) : (
        <p className="muted">This deck has no slides yet.</p>
      )}
      {slides.length > 0 ? (
        <PresentationDeckNav
          index={index}
          total={slides.length}
          onPrev={() => goTo(index - 1)}
          onNext={() => goTo(index + 1)}
          nextLabel="Last slide"
        />
      ) : null}
      <div className="teacher-quiz-editor-actions">
        {slides.length > 0 ? (
          <button type="button" className="btn primary" onClick={startPresenting}>
            Present
          </button>
        ) : null}
        {slides.length > 0 ? (
          <button
            type="button"
            className="btn secondary"
            onClick={() => triggerPresentationPdfDownload({ title: presentation.title, slides })}
          >
            Download PDF
          </button>
        ) : null}
        {editUrl ? (
          <Link className="btn secondary" to={editUrl}>
            Edit
          </Link>
        ) : null}
        <Link className="btn secondary" to={backUrl}>
          Back
        </Link>
      </div>
    </div>
  );
}
